import "../../../../assets/css/AddCatalogs.css";
import React, { useEffect, useState } from "react";
import CatalogSubTitle from "../../../../components/Moleculas/Catalog Text/CatalogSubTitle";
import NewButton from "../../../../components/Atomos/Buttons/newButton";
import RadioButton from "../../../../components/Atomos/Radio/RadioButton";
import DropDownList from "../../../../components/Atomos/DropDown/Dropdown";
import Close from "../../../../components/Atomos/Icons/Close";
import SaveChanges from "../../../../components/Atomos/Icons/EditMode/SaveChanges";
import ModalConfirmation from "./ModalConfirmation";
import { useDispatch, useSelector } from "react-redux";
import { Appstore, manageFirmSlice } from "../../../../redux";
import { changeFirmMembershipStatus } from "../../../../Services/Firm";
import { formatDateToString } from "../../../../utils/functions";
import { hideLoader, showLoader } from "../../../../redux/loaderActions";
import {
  MEMBERSHIP_STATUS,
  normalizeMembershipStatus,
  type MembershipStatusLabel,
} from "../../../../utils/membershipStatus";

interface IMainBoxUpdateMembershipProps {
  membershipDetails: any;
  onSaveMembership: (updatedDetails: any) => void;
  setUpdateMembership: React.Dispatch<React.SetStateAction<boolean>>;
}

const MainBoxUpdate: React.FC<IMainBoxUpdateMembershipProps> = ({
  onSaveMembership,
  setUpdateMembership,
  membershipDetails,
}) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = React.useState<
    MembershipStatusLabel | ""
  >(
    normalizeMembershipStatus(
      membershipDetails.membershipStatus ?? MEMBERSHIP_STATUS.Onboarding,
    ),
  );
  const [selectedMembershipType, setSelectedMembershipType] = React.useState(
    membershipDetails.membershipType ?? "",
  );
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [selectedWithdrawn, setSelectedWithdrawn] = React.useState<
    MembershipStatusLabel | ""
  >("");
  const [saveError, setSaveError] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);
  const [dueDate, setDueDate] = useState<Date>();
  const firm = manageFirm.selectedFirm;

  const updateFirmMembership = async () => {
    if (manageFirm.selectedFirm === undefined) {
      return;
    }

    setSaveError("");
    setIsSaving(true);
    showLoader();

    try {
      const normalizedStatus = normalizeMembershipStatus(selectedOption);
      const result = await changeFirmMembershipStatus({
        firmId: manageFirm.selectedFirm.id,
        membershipStatus: normalizedStatus,
        membershipType: selectedMembershipType,
      });

      const updatedFirm = {
        ...manageFirm.selectedFirm,
        ...result.firm,
        membershipStatus: normalizeMembershipStatus(
          result.firm?.membershipStatus ?? normalizedStatus,
        ),
        membershipType: selectedMembershipType,
      };

      dispatch(manageFirmSlice.actions.SetFirm(updatedFirm));
      onSaveMembership(updatedFirm);
    } catch (error) {
      console.error(error);
      setSaveError(
        "Unable to update membership status. Please try again or contact support.",
      );
    } finally {
      setIsSaving(false);
      hideLoader();
    }
  };

  const checkEditType = (type: any) => {
    if (type === 0) {
      setSelectedOption(MEMBERSHIP_STATUS.Accepted);
      return;
    }
    if (type === 1) {
      setSelectedOption(MEMBERSHIP_STATUS.Member);
      return;
    }
    if (type === 2) {
      setSelectedOption(MEMBERSHIP_STATUS.Onboarding);
      return;
    }
    if (type === 3) {
      setOpenModal(true);
      setSelectedWithdrawn(MEMBERSHIP_STATUS.TemporaryWithdrawn);
      return;
    }
    if (type === 4) {
      setOpenModal(true);
      setSelectedWithdrawn(MEMBERSHIP_STATUS.Withdrawn);
      return;
    }
  };

  const setConfirmation = (confirmation: any) => {
    if (confirmation) {
      setSelectedOption(selectedWithdrawn);
    }
  };

  const membershipTypeData = [
    { id: "primary", name: "Primary" },
    { id: "multiplePrimary", name: "Multiple Primary" },
    { id: "regional", name: "Regional" },
    { id: "secondary", name: "Secondary" },
  ];

  useEffect(() => {
    setSelectedOption(
      normalizeMembershipStatus(
        membershipDetails.membershipStatus ?? MEMBERSHIP_STATUS.Onboarding,
      ),
    );
    setSelectedMembershipType(membershipDetails.membershipType ?? "");
  }, [membershipDetails.membershipStatus, membershipDetails.membershipType]);

  useEffect(() => {
    if (!firm?.dateJoined || !firm?.membershipDues) {
      return;
    }

    const currentYear = new Date().getFullYear();
    const dueYear =
      firm.membershipDues === "Paid" ? currentYear : currentYear - 1;

    const _dueDate = new Date(firm.dateJoined);
    _dueDate.setFullYear(dueYear);

    setDueDate(_dueDate);
  }, [firm?.dateJoined, firm?.membershipDues]);

  return (
    <>
      <ModalConfirmation
        openModal={openModal}
        setOpenModal={setOpenModal}
        setConfirmation={setConfirmation}
        title="Membership Status"
        message="Please confirm that you intend to withdraw/temporary withdraw this firm."
      />
      <section className="flex w-full flex-col">
        {saveError ? (
          <div
            className="mb-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
            role="alert"
          >
            {saveError}
          </div>
        ) : null}
        <div className="grid flex-row w-full gap-5 items-stretch">
          <div>
            <div className="flex flex-col gap-3 mb-6">
              <CatalogSubTitle
                title={membershipDetails?.name}
                className="text-Default lg:text-[42px] md:text-[34px] text-[26px] not-italic font-medium leading-normal md:leading-[48px] lg:leading-[44px]"
              />
              <div className="flex flex-col gap-5">
                <span className="text-[#333] lg:text-[28px] md:text-[26px] text-lg not-italic font-bold  leading-normal md:leading-9 lg:leading-8">
                  {"Membership Status: "}
                </span>
                <div className="lg:flex lg:flex-wrap gap-5 grid md:grid-cols-2 grid-cols-1">
                  <RadioButton
                    index={0}
                    text={MEMBERSHIP_STATUS.Accepted}
                    selected={
                      selectedOption === MEMBERSHIP_STATUS.Accepted
                    }
                    handleRadioButtonChange={checkEditType}
                  />
                  <RadioButton
                    index={1}
                    text={MEMBERSHIP_STATUS.Member}
                    selected={selectedOption === MEMBERSHIP_STATUS.Member}
                    handleRadioButtonChange={checkEditType}
                  />
                  <RadioButton
                    index={2}
                    text={MEMBERSHIP_STATUS.Onboarding}
                    selected={
                      selectedOption === MEMBERSHIP_STATUS.Onboarding
                    }
                    handleRadioButtonChange={checkEditType}
                  />
                  <RadioButton
                    index={3}
                    text={MEMBERSHIP_STATUS.TemporaryWithdrawn}
                    selected={
                      selectedOption === MEMBERSHIP_STATUS.TemporaryWithdrawn
                    }
                    handleRadioButtonChange={checkEditType}
                  />
                  <RadioButton
                    index={4}
                    text={MEMBERSHIP_STATUS.Withdrawn}
                    selected={
                      selectedOption === MEMBERSHIP_STATUS.Withdrawn
                    }
                    handleRadioButtonChange={checkEditType}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-1 justify-center align-middle mb-5 self-stretch lg:absolute lg:right-10 lg:top-10 ">
              {membershipDetails.logo ? (
                <img
                  src={membershipDetails.logo}
                  alt="Wsg_Profile_Picture"
                  className="object-cover max-h-[174px] max-w-[330px]"
                />
              ) : (
                <div></div>
              )}
            </div>

            <div className="grid w-full grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div className="grid flex-col col-span-2 md:col-span-1 gap-2">
                <DropDownList
                  title="Membership Type"
                  data={membershipTypeData}
                  selectedObjst={selectedMembershipType}
                  seter={setSelectedMembershipType}
                  error={false}
                  titleClass="!text-lg !font-bold !text-gray-900"
                />
              </div>

              <div className="grid flex-col col-span-2 md:col-span-1 gap-2">
                <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg not-italic font-bold leading-normal md:leading-5 lg:leading-normal">
                  {"Service Type: "}
                </span>
                <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg  not-italic font-normal leading-6 lg:leading-7">
                  {membershipDetails?.serviceType}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid w-full col-start-5 grid-cols-4 gap-5 mb-10">
          <div className="grid flex-col col-span-4 md:col-span-2 lg:col-span-1 gap-2">
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg not-italic font-bold leading-normal md:leading-5 lg:leading-normal">
              Date Joined:
            </span>
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg  not-italic font-normal leading-6 lg:leading-7">
              {manageFirm.selectedFirm?.dateJoined
                ? formatDateToString(
                    new Date(manageFirm.selectedFirm.dateJoined),
                  )
                : ""}
            </span>
          </div>
          <div className="grid flex-col col-span-4 md:col-span-2 lg:col-span-1 gap-2">
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg not-italic font-bold leading-normal md:leading-5 lg:leading-normal">
              Membership Organization:
            </span>
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg  not-italic font-normal leading-6 lg:leading-7">
              {"70% Optimized"}
            </span>
          </div>
          <div className="grid flex-col col-span-4 md:col-span-2 lg:col-span-1 gap-2">
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg not-italic font-bold leading-normal md:leading-5 lg:leading-normal">
              Due Date:
            </span>
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg  not-italic font-normal leading-6 lg:leading-7">
              {formatDateToString(dueDate)}
            </span>
          </div>
          <div className="grid flex-col col-span-4 md:col-span-2 lg:col-span-1 gap-2">
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lg not-italic font-bold leading-normal md:leading-5 lg:leading-normal">
              Dues Payment Status:
            </span>
            <span className="text-gray-900 lg:text-[22px] md:text-[20px] text-lgs  not-italic font-normal leading-6 lg:leading-7">
              {manageFirm.selectedFirm?.membershipDues}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col gap-5 md:flex-row md:justify-end">
          <div className=" w-full md:w-min  ">
            <NewButton
              onClick={() => {
                setUpdateMembership(false);
              }}
              text={"Cancel"}
              className="!max-h-[35px] !max-w-[45px]"
              icon={<Close />}
              color="neutral"
              content="textIcon"
              size="large"
              style="outlined"
              disabled={isSaving}
            />
          </div>
          <div className="w-full md:w-min ">
            <NewButton
              text={"Save Changes"}
              icon={<SaveChanges />}
              onClick={() => {
                void updateFirmMembership();
              }}
              className="!max-h-[35px] !max-w-[45px]"
              color={"accent"}
              content="textIcon"
              size="large"
              style="filled"
              disabled={isSaving}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default MainBoxUpdate;
